import { Formik, Form } from 'formik';
import Loader from 'features/Loader/Loader';
import { roundNum } from 'utils/numbers';
import { mealSchema } from 'utils/validators/nutrition';
import React from 'react';
import confirmDiscardChanges from 'shared/components/confirm-discard-changes/confirmDiscardChanges';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import Title from 'shared/components/title/Title';
import MealTitleForm from './MealTitleForm';
import IngredientSection from './IngredientSection';
import MealTip from './MealTip';
import AddIngredient from '../Ingredient/AddIngredient';
import AddIngredientData from '../Ingredient/AddIngredientData';
import CreateFood from '../FoodCreate/CreateFood';

const steps = {
  INITIAL: 'initial',
  INGREDIENT_LIST: 'ingredientList',
  ADD_INGREDIENT: 'addIngredient',
  CREATE_FOOD: 'createFood'
};

class MealForm extends React.Component {
  constructor(props) {
    super(props);
    const { initialMeal, nutritionPlan } = props;

    this.state = {
      planId: nutritionPlan.id,
      showTip: initialMeal.tip !== '',
      step: steps.INITIAL,
      newIngredient: {},
      meal: initialMeal
    };

    this.handleReturnToInitialStep = this.handleReturnToInitialStep.bind(this);
    this.handleAddIngredient = this.handleAddIngredient.bind(this);
    this.handleSelectIngredient = this.handleSelectIngredient.bind(this);
    this.handleCreateNewFood = this.handleCreateNewFood.bind(this);
    this.handleAddTip = this.handleAddTip.bind(this);
    this.handleMealNameChange = this.handleMealNameChange.bind(this);
    this.handleTipChange = this.handleTipChange.bind(this);
    this.handleIngredientSubmission = this.handleIngredientSubmission.bind(
      this
    );
    this.handleRemovingIngredient = this.handleRemovingIngredient.bind(this);
  }

  handleReturnToInitialStep() {
    this.setState({ step: steps.INITIAL });
  }

  handleAddIngredient() {
    this.setState({ step: steps.INGREDIENT_LIST });
  }

  handleSelectIngredient(ingredient) {
    let { original_weight, original_calories, original_measure, original_quantity, weight_grams, calories, measurement_unit, quantity_size} = ingredient;
    let originalWeight = original_weight ? original_weight : weight_grams;
    let originalCalories = original_calories ? original_calories : calories;
    let originalMeasure = original_measure ? original_measure : measurement_unit;
    let originalQuantity = original_quantity ? Number(original_quantity) : (quantity_size ? Number(quantity_size) : 1);
    this.props.getMeasures(ingredient.name, originalWeight, originalCalories, originalMeasure, originalQuantity);
    ingredient.servingSize = ingredient.quantity_size;
    this.setState({
      newIngredient: ingredient,
      step: steps.ADD_INGREDIENT
    });
  }

  handleCreateNewFood() {
    this.setState({ step: steps.CREATE_FOOD });
  }

  handleAddTip() {
    this.setState({ showTip: true });
  }

  handleMealNameChange(e) {
    this.setState({
      meal: {
        ...this.state.meal,
        name: e.target.value
      }
    });
  }

  handleTipChange(e) {
    this.setState({
      meal: {
        ...this.state.meal,
        tip: e.target.value
      }
    });
  }

  handleIngredientSubmission(ingredient) {
    const {
      ingredients,
      ingredients_calories,
      ingredients_carbs,
      ingredients_fat,
      ingredients_protein
    } = this.state.meal;
    var updatedIngredients = [];
    var ingredientsCalories =
        Number(ingredients_calories) + ingredient.calories,
      ingredientsCarbs = Number(ingredients_carbs) + ingredient.carbs,
      ingredientsFat = Number(ingredients_fat) + ingredient.fat,
      ingredientsProtein = Number(ingredients_protein) + ingredient.protein;

    if (ingredient.isNew) {
      delete ingredient.isNew;
      delete ingredient.objectID;
      updatedIngredients = [...ingredients, ingredient];
    } else {
      const index = ingredient.index;
      delete ingredient.index;

      ingredientsCalories -= Number(ingredients[index].calories);
      ingredientsCarbs -= Number(ingredients[index].carbs);
      ingredientsFat -= Number(ingredients[index].fat);
      ingredientsProtein -= Number(ingredients[index].protein);
      updatedIngredients = ingredients.map((ing, i) =>
        index === i ? ingredient : ing
      );
    }
    this.setState({
      meal: {
        ...this.state.meal,
        ingredients_calories: roundNum(ingredientsCalories),
        ingredients_carbs: roundNum(ingredientsCarbs),
        ingredients_fat: roundNum(ingredientsFat),
        ingredients_protein: roundNum(ingredientsProtein),
        ingredients: updatedIngredients
      }
    });
    this.handleReturnToInitialStep();
  }

  handleRemovingIngredient(index) {
    const {
      ingredients,
      ingredients_calories,
      ingredients_carbs,
      ingredients_fat,
      ingredients_protein
    } = this.state.meal;

    this.setState({
      meal: {
        ...this.state.meal,
        ingredients_calories: roundNum(
          ingredients_calories - ingredients[index].calories
        ),
        ingredients_carbs: roundNum(
          ingredients_carbs - ingredients[index].carbs
        ),
        ingredients_fat: roundNum(ingredients_fat - ingredients[index].fat),
        ingredients_protein: roundNum(
          ingredients_protein - ingredients[index].protein
        ),
        ingredients: ingredients.filter((ing, i) => index !== i)
      }
    });
  }

  render() {
    const { step, showTip, newIngredient, meal } = this.state;
    const {
      history,
      nutritionPlan,
      updateMeal,
      pageTitle,
      headerTitle,
      initialMeal,
      measurementsUnits,
      loadingMeasurements
    } = this.props;

    const mealNames =
      nutritionPlan &&
      nutritionPlan.meal_items &&
      nutritionPlan.meal_items
        .filter(m => m.name !== meal.name)
        .map(i => i.name);

    switch (step) {
      case steps.INGREDIENT_LIST:
        return (
          <AddIngredient
            nutritionPlan={nutritionPlan}
            headerTitle={headerTitle}
            handleReturnToInitialStep={this.handleReturnToInitialStep}
            handleCreateNewFood={this.handleCreateNewFood}
            handleSelectIngredient={this.handleSelectIngredient}
          />
        );
      case steps.ADD_INGREDIENT:
        return (
          <AddIngredientData
            initialIngredient={newIngredient}
            nutritionPlan={nutritionPlan}
            headerTitle={headerTitle}
            handleReturnToInitialStep={this.handleReturnToInitialStep}
            handleIngredientSubmission={this.handleIngredientSubmission}
            measurementsUnits={measurementsUnits}
            loadingMeasurements={loadingMeasurements}
          />
        );
      case steps.CREATE_FOOD:
        return (
          <CreateFood
            nutritionPlan={nutritionPlan}
            headerTitle={headerTitle}
            handleReturnToInitialStep={this.handleReturnToInitialStep}
            handleIngredientSubmission={this.handleIngredientSubmission}
          />
        );
      default:
        return (
          <div className="create-meal">
            <div className="container">
              <Breadcrumb>
                <Breadcrumb.Item href="/nutrition">Nutrition</Breadcrumb.Item>
                <Breadcrumb.Item href={`/nutrition/${nutritionPlan.id}`}>
                  {nutritionPlan.name}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{headerTitle}</Breadcrumb.Item>
              </Breadcrumb>
              <Formik
                enableReinitialize={true}
                initialValues={{
                  name: meal.name,
                  tip: initialMeal.tip
                }}
                onSubmit={values => {
                  meal.name = values.name;
                  meal.tip = values.tip;
                  updateMeal(nutritionPlan, meal, history);
                }}
                validationSchema={() => mealSchema(mealNames)}
              >
                {formik => (
                  <Form>
                    {formik.isSubmitting ? (
                      <Loader />
                    ) : (
                      <div>
                        <Title
                          pageTitle={pageTitle}
                          buttonText="Save"
                          linkText="Cancel"
                          cancelLink={`/nutrition/${nutritionPlan.id}`}
                          cancelBtn={
                            (formik.dirty ||
                              JSON.stringify(initialMeal.ingredients) !==
                                JSON.stringify(meal.ingredients)) &&
                            (() =>
                              confirmDiscardChanges(
                                `/nutrition/${nutritionPlan.id}`,
                                history
                              ))
                          }
                        />
                        <MealTitleForm
                          meal={meal}
                          handleMealNameChange={this.handleMealNameChange}
                        />
                        <IngredientSection
                          ingredients={meal.ingredients}
                          handleAddIngredient={this.handleAddIngredient}
                          handleSelectIngredient={this.handleSelectIngredient}
                          handleRemovingIngredient={
                            this.handleRemovingIngredient
                          }
                          handleAddTip={this.handleAddTip}
                          showTip={showTip}
                        />
                        {showTip && (
                          <MealTip handleTipChange={this.handleTipChange} />
                        )}
                      </div>
                    )}
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        );
    }
  }
}

export default MealForm;
